import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { ICurrentCourse } from "../../shared/entities/enrollments";
import { ICoursesContract } from "../../courses/model";
import { routePathNameChanged } from "../../app/model";
import { IEnrollmentsContract } from "../../enrollments/model";
import { IUserAuthContract } from "../../student/auth/model";
import { FeedbackSubmitted, trackRelatedCoursesClick, trackRelatedCoursesShown } from "@features/shared/events";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { FeedbackRequestRecord } from "@features/shared/entities/lessons";
import { IRecommendedCourse } from "@features/shared/entities/courses";

export const currentCourseChanged = createEvent<ICurrentCourse | null>();
export const currentCourseIdChanged = createEvent<number>();

export const courseFeedbackOpened = createEvent<number>();
export const courseFeedbackHidden = createEvent();

export const setCurrentCourseFeedbackStep = createEvent<number>();

const rateCourse = createEvent<number>();
const courseFeedbackMessgaeChanged = createEvent<string>();
const selectFeedbackType = createEvent<string>();

export const trackCourseFeedback = createEvent();
const setCourseFeebackModalVisible = createEvent<boolean>();
const setSimilarCoursesModalVisible = createEvent<boolean>();

export const $currentCourse = createStore<ICurrentCourse | null>(null);
export const $currentCourseId = createStore<number | null>(null);

const $currentCourseFeedback = createStore<null | number>(null)
    .on(courseFeedbackOpened, (s, p) => p)
    .reset(courseFeedbackHidden);
const $currentCourseRating = createStore<null | number>(null)
    .on(rateCourse, (_, rating) => rating)
    .reset(courseFeedbackHidden, routePathNameChanged);
const $currentCourseFeedbackMessage = createStore("")
    .on(courseFeedbackMessgaeChanged, (s, p) => p)
    .reset(courseFeedbackHidden, routePathNameChanged);
const $currentCourseFeedbackStep = createStore<number>(0)
    .on(setCurrentCourseFeedbackStep, (s, p) => p)
    .reset(courseFeedbackHidden, routePathNameChanged);
const $currentCourseFeedbackType = createStore<null | string>(null)
    .on(selectFeedbackType, (_, p) => p)
    .reset(routePathNameChanged);
const $isCourseFeedbackModalVisble = createStore(false)
    .on(setCourseFeebackModalVisible, (_, p) => p)
    .reset(routePathNameChanged);
const $isSimilarCoursesModalVisible = createStore(false)
    .on(setSimilarCoursesModalVisible, (_, p) => p)
    .reset(routePathNameChanged);
const $recommendedCourses = createStore<IRecommendedCourse[]>([]);

const trackCourseFeedbackFx = createEffect<
    { courseId: number; comment: string; rating: number; commentType?: string },
    void
>(({ courseId, comment, rating, commentType }) => {
    FeedbackSubmitted({ courseId, type: "course_feedback", rating, comment, commentType });
});

const sendCourseFeedbackFx = createEffect(async (data: FeedbackRequestRecord) => {
    const response = await CourseDataApi.sendFeedback(data);

    return response;
});

const getCoursesRecommendationsFx = createEffect(async (courseId: number) => {
    const response = await CourseDataApi.getCoursesRecommendations(courseId);

    return response;
});

export const trackRelatedCoursesShownFx = createEffect(
    ({
        courseId,
        courseType,
        placement
    }: {
        courseId: number;
        courseType: "challenge" | "course";
        placement: string;
    }) => {
        trackRelatedCoursesShown(courseId, courseType, placement);
    }
);

const trackRelatedCoursesClickFx = createEffect(
    ({
        courseId,
        courseType,
        placement,
        target
    }: {
        courseId: number;
        courseType: "challenge" | "course";
        placement: string;
        target: number;
    }) => {
        trackRelatedCoursesClick(courseId, courseType, placement, target);
    }
);

sample({
    clock: currentCourseChanged,
    target: $currentCourse
});

sample({
    clock: currentCourseIdChanged,
    target: $currentCourseId
});

sample({
    clock: $currentCourseId,
    filter: (s) => s !== null,
    fn: (s) => s as number,
    target: getCoursesRecommendationsFx
});

sample({
    source: {
        courseId: $currentCourseId,
        comment: $currentCourseFeedbackMessage,
        rating: $currentCourseRating,
        commentType: $currentCourseFeedbackType
    },
    clock: trackCourseFeedback,
    filter: ({ courseId, rating }) => courseId !== null && rating !== null,
    fn: ({ courseId, comment, rating, commentType }) => {
        return {
            courseId: courseId as number,
            comment,
            rating: rating as number,
            commentType: commentType ?? ""
        };
    },
    target: trackCourseFeedbackFx
});

sample({
    source: {
        courseId: $currentCourseId,
        comment: $currentCourseFeedbackMessage,
        rating: $currentCourseRating
    },
    clock: trackCourseFeedback,
    filter: ({ courseId, rating }) => courseId !== null && rating !== null,
    fn: ({ courseId, comment, rating }) => {
        return {
            courseId: courseId as number,
            comment,
            rating: rating as number
        };
    },
    target: sendCourseFeedbackFx
});

sample({
    clock: $isSimilarCoursesModalVisible,
    source: {
        currentCourse: $currentCourse,
        isSimilarCoursesModalVisible: $isSimilarCoursesModalVisible
    },
    filter: ({ isSimilarCoursesModalVisible }) => isSimilarCoursesModalVisible,
    fn: ({ currentCourse }) => ({
        courseId: currentCourse?.id ?? 0,
        courseType: currentCourse?.type ?? "course",
        placement: "course_complete"
    }),
    target: trackRelatedCoursesShownFx
});

const contract = new ModelContract(
    {
        $currentCourse,
        $currentCourseId,
        $currentCourseFeedback,
        $currentCourseRating,
        $currentCourseFeedbackMessage,
        $currentCourseFeedbackStep,
        $currentCourseFeedbackType,
        $isCourseFeedbackModalVisble,
        $isSimilarCoursesModalVisible,
        $recommendedCourses
    },
    {
        currentCourseChanged,
        currentCourseIdChanged,
        courseFeedbackOpened,
        courseFeedbackHidden,
        rateCourse,
        courseFeedbackMessgaeChanged,
        trackCourseFeedback,
        setCurrentCourseFeedbackStep,
        selectFeedbackType,
        setCourseFeebackModalVisible,
        setSimilarCoursesModalVisible
    },
    { trackRelatedCoursesShownFx, trackRelatedCoursesClickFx },
    {}
);

export type ICurrentCourseContract = typeof contract;

export class CurrentCourseModule extends ModuleDescription<ICurrentCourseContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public coursesModule: ICoursesContract,
        public enrollmentsModule: IEnrollmentsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: getCoursesRecommendationsFx.doneData,
            target: $recommendedCourses
        });

        sample({
            clock: [
                currentCourseIdChanged,
                this.coursesModule.events.coursesLoaded,
                this.enrollmentsModule.effects.getEnrollmentsCoursesFx.doneData,
                this.coursesModule.events.draftCoursesLoaded
            ],
            source: {
                courseId: $currentCourseId,
                courses: this.coursesModule.stores.$courses,
                draftCourses: this.coursesModule.stores.$draftCourses,
                enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses
            },
            filter: ({ courseId }) => {
                return courseId !== null;
            },
            fn: ({ courseId, courses, draftCourses, enrollmentsCourses }) => {
                let course = courses.find((c) => c.id === courseId);

                if (!course) {
                    course = draftCourses.find((c) => c.id === courseId);
                }

                if (!course) return null;

                const enrollmentCourse = enrollmentsCourses.find((e) => e.course_id === courseId);

                return enrollmentCourse ? { ...enrollmentCourse, ...course } : course;
            },
            target: currentCourseChanged
        });

        sample({
            source: {
                courseId: $currentCourseId,
                courses: this.coursesModule.stores.$courses
            },
            clock: [currentCourseIdChanged, this.coursesModule.events.coursesLoaded],
            filter: ({ courseId, courses }) => {
                return courseId !== null && courses.length > 0 && !courses.find((c) => c.id === courseId);
            },
            target: this.coursesModule.effects.getDraftCoursesFx
        });

        sample({
            clock: routePathNameChanged,
            filter: (url: string) =>
                !!["course/", "challenge/"].find((target) => url.includes(target)) &&
                /^\d+$/.test(url.split("/")[2] ?? ""),
            fn: (url: string) => Number(url.split("/")[2]),
            target: currentCourseIdChanged
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [$currentCourse]
        });
    }
}
